/* Baby In personalized products: Coloring book (one item per child) and Magic Me (story packs).
 *
 * Two layers:
 *  - Core: pure logic (validation, the Magic Me story/child store, pack lookup, cart properties).
 *    No DOM, no storage, no network. Exported for Node tests.
 *  - Pages: progressive enhancement of the section markup. The real <form> posts multipart/form-data
 *    to /cart/add natively, so the actual photo File reaches Shopify as a line-item property.
 *
 * Nothing is written to localStorage/sessionStorage/cookies and nothing is sent anywhere except the
 * store's own /cart/add when the customer submits. All user text goes into the DOM via textContent.
 */
(function (global) {
  'use strict';

  // ---------------------------------------------------------------- Core
  var PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
  var PHOTO_EXT = /\.(jpe?g|png|webp)$/i;
  var MAX_PHOTO_BYTES = 10 * 1024 * 1024;
  var MIN_AGE = 1, MAX_AGE = 17, DEFAULT_AGE = '5', MAX_NAME = 40, MAX_CHILDREN = 3;
  var GENDERS = ['b', 'g'];

  function fmt(str, params) {
    return String(str == null ? '' : str).replace(/\{(\w+)\}/g, function (m, k) {
      return params && params[k] != null ? String(params[k]) : m;
    });
  }
  function toAsciiDigits(v) {
    return String(v == null ? '' : v)
      .replace(/[٠-٩]/g, function (d) { return String(d.charCodeAt(0) - 0x660); })
      .replace(/[۰-۹]/g, function (d) { return String(d.charCodeAt(0) - 0x6F0); });
  }
  function cleanName(v) { return String(v == null ? '' : v).replace(/\s+/g, ' ').trim(); }
  function ageOk(v) {
    var s = toAsciiDigits(v).trim(), a = Number(s);
    return s !== '' && Number.isInteger(a) && a >= MIN_AGE && a <= MAX_AGE;
  }
  // Locale-aware "5 years" text; strings carry {n}.
  function ageText(v, S, num) {
    var n = Number(toAsciiDigits(v)), f = num || String;
    var key = n === 1 ? 'age_one' : n === 2 ? 'age_two' : n <= 10 ? 'age_few' : 'age_many';
    return fmt((S && S[key]) || '{n}', { n: f(n) });
  }
  // '' when acceptable, else an error code. Browsers sometimes report an empty type; fall back to the extension.
  function checkPhoto(file) {
    if (!file) return 'photo_missing';
    var type = String(file.type || '').toLowerCase();
    var okType = type ? PHOTO_TYPES.indexOf(type) !== -1 : PHOTO_EXT.test(String(file.name || ''));
    if (!okType) return 'photo_type';
    if (!(file.size > 0)) return 'photo_empty';
    if (file.size > MAX_PHOTO_BYTES) return 'photo_size';
    return '';
  }

  // Coloring book: one child per cart item. Returns the first problem in reading order, or null.
  function validateColoring(d) {
    if (!d || GENDERS.indexOf(d.gender) === -1) return { field: 'gender', code: 'gender_missing' };
    var name = cleanName(d.name);
    if (!name) return { field: 'name', code: 'name_missing' };
    if (name.length > MAX_NAME) return { field: 'name', code: 'name_long' };
    if (!ageOk(d.age)) return { field: 'age', code: 'age_missing' };
    var p = checkPhoto(d.file);
    if (p) return { field: 'photo', code: p };
    if (!d.consent) return { field: 'consent', code: 'consent_missing' };
    if (!d.variantAvailable) return { field: 'submit', code: 'unavailable' };
    return null;
  }

  // Magic Me packs: find the real variant for each story count. Matching order: expected option value
  // (digits normalized), then exact expected price, never an invented id. Missing => unavailable.
  function resolvePacks(variants, expected) {
    var list = Array.isArray(variants) ? variants : [], used = {}, out = {};
    var norm = function (s) { return toAsciiDigits(s).replace(/\s+/g, ' ').trim(); };
    (expected || []).forEach(function (e) {
      var want = norm(e.value);
      var hit = list.find(function (v) {
        if (used[v.id]) return false;
        return [v.option1, v.option2, v.option3, v.title].some(function (o) { return o != null && norm(o) === want; });
      }) || list.find(function (v) { return !used[v.id] && Number(v.price) === Number(e.price); });
      if (hit) {
        used[hit.id] = true;
        out[e.count] = { count: e.count, id: hit.id, price: Number(hit.price), priceText: hit.price_text || '',
          available: hit.available !== false, name: e.label || e.value, matchedBy: [hit.option1, hit.option2, hit.option3, hit.title].some(function (o) { return o != null && norm(o) === want; }) ? 'option' : 'price' };
      } else {
        out[e.count] = null;
      }
    });
    var single = out[1];
    Object.keys(out).forEach(function (k) {
      var p = out[k]; if (!p) return;
      p.unit = Math.round(p.price / p.count);
      p.saving = single && p.count > 1 ? Math.max(0, single.price * p.count - p.price) : 0;
    });
    return out;
  }

  // Magic Me state: three story items always exist; the first `count` are active. Inactive ones keep their state.
  function createMagicStore(templates, opts) {
    var T = templates || [], seq = 0, maxCount = (opts && opts.maxCount) || 3;
    var template = function (id) { return T.find(function (t) { return t.id === id; }); };
    var blankChild = function () { return { id: 'c' + (++seq), name: '', age: DEFAULT_AGE, gender: '', hasPhoto: false, photoUrl: '' }; };
    var first = blankChild();
    var s = { count: 1, children: [first], items: [1, 2, 3].map(function (n) { return { id: 's' + n, childId: first.id, templateId: '' }; }), consent: false };

    var child = function (id) { return s.children.find(function (c) { return c.id === id; }); };
    var item = function (id) { return s.items.find(function (i) { return i.id === id; }); };
    var active = function () { return s.items.slice(0, s.count); };
    var storyIndex = function (it) { return s.items.indexOf(it); };
    var activeChildren = function () {
      var seen = [];
      active().forEach(function (it) { var c = child(it.childId); if (c && seen.indexOf(c) === -1) seen.push(c); });
      return seen;
    };
    var homeOf = function (childId) { return active().find(function (it) { return it.childId === childId; }); };
    var isComplete = function (c) { return !!(cleanName(c.name) && ageOk(c.age) && c.gender && c.hasPhoto); };
    var isBlank = function (c) { return !cleanName(c.name) && !c.gender && !c.hasPhoto; };
    var duplicateOf = function (it, childId, templateId) {
      return templateId ? active().find(function (o) { return o !== it && o.childId === childId && o.templateId === templateId; }) : undefined;
    };
    var prune = function () {
      s.children = s.children.filter(function (c) { return s.items.some(function (i) { return i.childId === c.id; }) || !isBlank(c); });
    };
    var itemProblem = function (it) {
      var c = child(it.childId), at = { itemId: it.id, story: storyIndex(it) };
      if (!c) return Object.assign({ code: 'who_missing', field: 'who' }, at);
      var withChild = Object.assign({ childId: c.id }, at);
      if (!cleanName(c.name)) return Object.assign({ code: 'name_missing', field: 'name' }, withChild);
      if (!ageOk(c.age)) return Object.assign({ code: 'age_missing', field: 'age' }, withChild);
      if (!c.gender) return Object.assign({ code: 'gender_missing', field: 'gender' }, withChild);
      if (!c.hasPhoto) return Object.assign({ code: 'photo_missing', field: 'photo' }, withChild);
      var t = template(it.templateId);
      if (!t || t.gender !== c.gender) return Object.assign({ code: 'cover_missing', field: 'cover' }, withChild);
      var dup = duplicateOf(it, c.id, t.id);
      if (dup && storyIndex(dup) < storyIndex(it)) return Object.assign({ code: 'cover_duplicate', field: 'cover', templateId: t.id, other: storyIndex(dup) }, withChild);
      return null;
    };

    return {
      state: s, child: child, item: item, active: active, activeChildren: activeChildren, homeOf: homeOf,
      template: template, storyIndex: storyIndex, isComplete: isComplete, itemProblem: itemProblem,
      itemComplete: function (it) { return !itemProblem(it); },
      setCount: function (n) { n = Number(n); if (n >= 1 && n <= maxCount) s.count = n; },
      canAddChild: function () { return s.children.length < MAX_CHILDREN; },
      // Returns a note code when the story's cover had to be cleared.
      assign: function (itemId, childId) {
        var it = item(itemId), c = child(childId); if (!it || !c) return '';
        it.childId = c.id; var note = '';
        var t = template(it.templateId);
        if (t && t.gender !== c.gender) { it.templateId = ''; note = c.gender ? 'cover_cleared_gender' : 'need_gender_first'; }
        else if (t && duplicateOf(it, c.id, t.id)) { it.templateId = ''; note = 'cover_cleared_duplicate'; }
        prune(); return note;
      },
      assignNewChild: function (itemId) {
        if (s.children.length >= MAX_CHILDREN) return { error: 'max_children' };
        var c = blankChild(); s.children.push(c);
        return { child: c, note: this.assign(itemId, c.id) };
      },
      setField: function (childId, field, value) {
        var c = child(childId); if (c && (field === 'name' || field === 'age')) c[field] = String(value);
      },
      setGender: function (childId, g) {
        var c = child(childId); if (!c || GENDERS.indexOf(g) === -1) return [];
        c.gender = g; var cleared = [];
        s.items.forEach(function (it) {
          var t = template(it.templateId);
          if (it.childId === c.id && t && t.gender !== g) { it.templateId = ''; if (active().indexOf(it) !== -1) cleared.push(it); }
        });
        return cleared;
      },
      pick: function (itemId, templateId) {
        var it = item(itemId), t = template(templateId); if (!it || !t) return 'pick_invalid';
        var c = child(it.childId);
        if (!c || c.gender !== t.gender) return 'pick_wrong_gender';
        if (duplicateOf(it, c.id, t.id)) return 'pick_duplicate';
        it.templateId = t.id; return '';
      },
      // Validates the file; on success swaps the preview URL. Invalid files leave the old photo in place.
      setPhoto: function (childId, file, createURL, revokeURL) {
        var c = child(childId); if (!c || !file) return 'photo_missing';
        var err = checkPhoto(file); if (err) return err;
        if (c.photoUrl && revokeURL) revokeURL(c.photoUrl);
        c.photoUrl = createURL ? createURL(file) : ''; c.hasPhoto = true; return '';
      },
      validate: function () {
        var list = active();
        for (var i = 0; i < list.length; i++) { var p = itemProblem(list[i]); if (p) return p; }
        if (!s.consent) return { code: 'consent_missing', field: 'consent' };
        return null;
      }
    };
  }

  // What the Magic Me cart line carries. Human-readable properties per story and per child, the photo
  // file slot name for each distinct active child, plus a hidden machine-readable summary.
  function buildMagicSubmission(store, S, num) {
    var f = num || String, s = store.state, kids = store.activeChildren();
    var gw = function (g) { return g === 'g' ? S.gender_g : S.gender_b; };
    var props = [];
    var childNo = function (c) { return kids.indexOf(c) + 1; };
    props.push([S.prop_pack, S['pack_' + s.count] || String(s.count)]);
    store.active().forEach(function (it, i) {
      var c = store.child(it.childId), t = store.template(it.templateId);
      props.push([fmt(S.prop_story, { n: f(i + 1) }), fmt(S.story_value, { title: t ? t.title : '', name: cleanName(c.name), n: f(childNo(c)) })]);
    });
    kids.forEach(function (c, j) {
      props.push([fmt(S.prop_child, { n: f(j + 1) }), fmt(S.child_value, { name: cleanName(c.name), gender: gw(c.gender), age: ageText(c.age, S, f) })]);
    });
    props.push([S.prop_consent, S.consent_value]);
    var summary = {
      v: 1, count: s.count,
      stories: store.active().map(function (it) { return { template: it.templateId, child: childNo(store.child(it.childId)) }; }),
      children: kids.map(function (c, j) { return { n: j + 1, name: cleanName(c.name), age: Number(toAsciiDigits(c.age)), gender: c.gender }; })
    };
    props.push(['_bip_order', JSON.stringify(summary)]);
    return {
      properties: props,
      files: kids.map(function (c, j) { return { childId: c.id, name: 'properties[' + fmt(S.prop_photo, { n: f(j + 1) }) + ']' }; })
    };
  }

  // Photo inputs are created once per child and never re-rendered. Before submit, active children's
  // inputs get their property name; inactive ones are disabled so they are not uploaded.
  // `slots` is a Map childId -> input; `memory` a Map childId -> last valid File; `restore(input, file)`
  // puts a File back into an input (DataTransfer) and returns true on success.
  function prepareFileSlots(slots, files, memory, restore) {
    var wanted = {}, missing = [];
    files.forEach(function (f) { wanted[f.childId] = f.name; });
    slots.forEach(function (input, childId) {
      if (wanted[childId]) {
        input.disabled = false; input.setAttribute('name', wanted[childId]);
        var has = input.files && input.files.length === 1;
        if (!has && memory && memory.get(childId) && restore) has = restore(input, memory.get(childId));
        if (!has) missing.push(childId);
      } else {
        input.removeAttribute('name'); input.disabled = true;
      }
    });
    files.forEach(function (f) { if (!slots.has(f.childId)) missing.push(f.childId); });
    return missing;
  }

  var Core = {
    PHOTO_TYPES: PHOTO_TYPES, MAX_PHOTO_BYTES: MAX_PHOTO_BYTES, MIN_AGE: MIN_AGE, MAX_AGE: MAX_AGE, DEFAULT_AGE: DEFAULT_AGE,
    fmt: fmt, toAsciiDigits: toAsciiDigits, cleanName: cleanName, ageOk: ageOk, ageText: ageText, checkPhoto: checkPhoto,
    validateColoring: validateColoring, resolvePacks: resolvePacks, createMagicStore: createMagicStore,
    buildMagicSubmission: buildMagicSubmission, prepareFileSlots: prepareFileSlots
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = Core;
  global.BabyInPersonalized = Core;
  if (typeof document === 'undefined') return;

  // ---------------------------------------------------------------- Shared DOM helpers
  var svgNS = 'http://www.w3.org/2000/svg';
  function icon(d) {
    var s = document.createElementNS(svgNS, 'svg');
    [['width', '16'], ['height', '16'], ['viewBox', '0 0 24 24'], ['aria-hidden', 'true'], ['focusable', 'false']].forEach(function (a) { s.setAttribute(a[0], a[1]); });
    var p = document.createElementNS(svgNS, 'path');
    [['d', d], ['fill', 'none'], ['stroke', 'currentColor'], ['stroke-width', '3'], ['stroke-linecap', 'round'], ['stroke-linejoin', 'round']].forEach(function (a) { p.setAttribute(a[0], a[1]); });
    s.appendChild(p); return s;
  }
  var tickIcon = function () { return icon('M5 12.5l4.5 4.5L19 7.5'); };
  var plusIcon = function () { return icon('M12 5v14M5 12h14'); };
  var photoIcon = function () { return icon('M4 7h3l2-3h6l2 3h3v13H4zM12 17a4 4 0 1 0 0-8 4 4 0 0 0 0 8'); };
  var reduceMotion = function () { return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches; };
  function numberFormatter(locale) {
    return function (n) { try { return Number(n).toLocaleString(locale || 'ar-EG'); } catch (e) { return String(n); } };
  }
  // Put a remembered File back into an <input type=file>. Returns true when the input holds it.
  function restoreFile(input, file) {
    try {
      if (typeof DataTransfer === 'undefined') return false;
      var dt = new DataTransfer(); dt.items.add(file); input.files = dt.files;
      return input.files && input.files.length === 1;
    } catch (e) { return false; }
  }
  // Native submission that skips submit listeners (theme/app AJAX handlers never see it).
  function nativeSubmit(form) { HTMLFormElement.prototype.submit.call(form); }
  // Shopify's `t` filter HTML-escapes translations; decode once (inert <textarea>, never executed), then
  // every string reaches the page through textContent/attributes only.
  function decodeEntities(str) {
    if (typeof str !== 'string' || str.indexOf('&') === -1) return str;
    var ta = document.createElement('textarea'); ta.innerHTML = str; return ta.value;
  }
  function readConfig(root) {
    var node = root.querySelector('script[data-bip-config]'), cfg;
    try { cfg = JSON.parse(node ? node.textContent : '{}'); } catch (e) { return {}; }
    Object.keys(cfg.strings || {}).forEach(function (k) { cfg.strings[k] = decodeEntities(cfg.strings[k]); });
    (cfg.templates || []).forEach(function (t) { t.title = decodeEntities(t.title); t.alt = decodeEntities(t.alt); });
    return cfg;
  }
  function setBusy(buttons, busy) {
    buttons.forEach(function (b) { if (!b) return; b.disabled = busy; if (busy) b.setAttribute('aria-busy', 'true'); else b.removeAttribute('aria-busy'); });
  }
  function scrollTo(el) { if (el && el.scrollIntoView) el.scrollIntoView({ block: 'start', behavior: reduceMotion() ? 'auto' : 'smooth' }); }
  function setSteps(root, n) {
    root.querySelectorAll('[data-bip-stepdot]').forEach(function (li) {
      var k = Number(li.getAttribute('data-bip-stepdot'));
      li.setAttribute('data-state', k < n ? 'done' : k === n ? 'current' : 'todo');
      if (k === n) li.setAttribute('aria-current', 'step'); else li.removeAttribute('aria-current');
    });
    root.querySelectorAll('[data-bip-step]').forEach(function (p) { p.hidden = Number(p.getAttribute('data-bip-step')) !== n; });
  }
  // Edition switch for the sample gallery (coloring). Buttons: [data-bip-edition], panels: [data-bip-edition-panel].
  function setEdition(root, g) {
    root.querySelectorAll('[data-bip-edition]').forEach(function (b) { b.setAttribute('aria-pressed', String(b.getAttribute('data-bip-edition') === g)); });
    root.querySelectorAll('[data-bip-edition-panel]').forEach(function (p) { p.hidden = p.getAttribute('data-bip-edition-panel') !== g; });
  }

  // ---------------------------------------------------------------- Coloring book
  function initColoring(root, cfg) {
    var S = cfg.strings || {}, num = numberFormatter(cfg.numLocale), preview = cfg.mode === 'preview';
    var form = root.querySelector('[data-bip-form]'); if (!form) return;
    var q = function (sel) { return form.querySelector(sel); };
    var nameIn = q('[data-bip-name]'), ageIn = q('[data-bip-age]'), ageOut = q('[data-bip-age-out]');
    var photoIn = q('[data-bip-photo]'), zone = q('[data-bip-drop]'), thumb = q('[data-bip-thumb]'), zoneText = q('[data-bip-drop-text]');
    var photoErr = q('[data-bip-photo-error]'), consent = q('[data-bip-consent]'), errorEl = q('[data-bip-error]');
    var statusEl = q('[data-bip-status]'), review = q('[data-bip-review]'), backBtn = q('[data-bip-back]');
    var nextBtn = q('[data-bip-next]'), addBtn = q('[data-bip-add]'), anotherBtn = q('[data-bip-another]');
    var returnTo = q('[data-bip-return]'), variantIn = q('input[name="id"]');
    var genders = Array.prototype.slice.call(form.querySelectorAll('[data-bip-gender]'));
    var step = 1, lastFile = null, photoUrl = '', wantAnother = false;

    var gender = function () { var g = genders.find(function (r) { return r.checked; }); return g ? g.getAttribute('data-bip-gender') : ''; };
    var setError = function (msg) { errorEl.textContent = msg || ''; };
    var setStatus = function (msg) { if (statusEl) statusEl.textContent = msg || ''; };

    function showPhoto(file) {
      if (photoUrl) URL.revokeObjectURL(photoUrl);
      photoUrl = file ? URL.createObjectURL(file) : '';
      thumb.replaceChildren(photoUrl ? Object.assign(document.createElement('img'), { src: photoUrl, alt: S.photo_alt || '' }) : photoIcon());
      zone.setAttribute('data-has', String(!!file));
      zoneText.textContent = file ? S.photo_change : S.photo_pick;
    }
    function takeFile(file, fromDrop) {
      if (!file) return;
      var err = checkPhoto(file);
      if (err) {
        photoErr.textContent = S[err] || '';
        // Keep the last good photo in the input, so a bad pick never loses it.
        if (lastFile) { if (!restoreFile(photoIn, lastFile)) photoIn.value = ''; } else photoIn.value = '';
        return;
      }
      if (fromDrop && !restoreFile(photoIn, file)) { photoErr.textContent = S.photo_drop_failed || ''; return; }
      photoErr.textContent = ''; lastFile = file; showPhoto(file); setError('');
    }
    photoIn.addEventListener('change', function () { takeFile(photoIn.files && photoIn.files[0], false); });
    zone.addEventListener('dragover', function (e) { e.preventDefault(); zone.setAttribute('data-over', 'true'); });
    zone.addEventListener('dragleave', function () { zone.setAttribute('data-over', 'false'); });
    zone.addEventListener('drop', function (e) {
      e.preventDefault(); zone.setAttribute('data-over', 'false');
      takeFile(e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0], true);
    });

    function syncAge() {
      var t = ageText(ageIn.value, S, num); ageOut.textContent = t; ageIn.setAttribute('aria-valuetext', t);
    }
    ageIn.addEventListener('input', syncAge); syncAge();
    // data-checked mirrors :checked for browsers without :has().
    var markChoices = function () { genders.forEach(function (r) { var l = r.closest('.bip-choice'); if (l) l.setAttribute('data-checked', String(r.checked)); }); };
    genders.forEach(function (r) { r.addEventListener('change', function () { markChoices(); setEdition(root, gender()); setError(''); }); });
    markChoices();
    root.querySelectorAll('[data-bip-edition]').forEach(function (b) {
      b.addEventListener('click', function () { setEdition(root, b.getAttribute('data-bip-edition')); });
    });

    function current() {
      var file = photoIn.files && photoIn.files[0] ? photoIn.files[0] : null;
      if (!file && lastFile && restoreFile(photoIn, lastFile)) file = lastFile;
      return { gender: gender(), name: nameIn.value, age: ageIn.value, file: file, consent: consent.checked,
        variantAvailable: !!(variantIn && variantIn.value && cfg.variant && cfg.variant.available) };
    }
    function showProblem(p) {
      setError(S[p.code] || '');
      if (step !== 1 && p.field !== 'submit') goStep(1, true);
      var target = { gender: genders[0], name: nameIn, age: ageIn, photo: photoIn, consent: consent }[p.field];
      if (target) { target.focus(); if (target.scrollIntoView) target.scrollIntoView({ block: 'center' }); }
    }
    function goStep(n, keepError) {
      step = n; setSteps(root, n);
      backBtn.hidden = n === 1; nextBtn.hidden = n !== 1;
      addBtn.hidden = n !== 2; if (anotherBtn) anotherBtn.hidden = n !== 2;
      if (!keepError) setError('');
      setStatus('');
      var h = root.querySelector('[data-bip-steptitle="' + n + '"]'); if (h) h.focus();
      scrollTo(form);
    }
    function renderReview(d) {
      var row = function (dt, dd) {
        var r = document.createElement('div'); r.className = 'bip-row';
        var a = document.createElement('dt'); a.textContent = dt;
        var b = document.createElement('dd'); if (typeof dd === 'string') b.textContent = dd; else b.appendChild(dd);
        r.append(a, b); return r;
      };
      var head = document.createElement('div'); head.className = 'bip-review-child';
      var img = document.createElement('img'); img.src = photoUrl; img.alt = S.photo_alt || ''; img.width = 72; img.height = 72;
      var txt = document.createElement('div');
      var b = document.createElement('b'); b.textContent = cleanName(d.name);
      var sm = document.createElement('span'); sm.textContent = fmt(S.review_child_line, { edition: d.gender === 'g' ? S.edition_g : S.edition_b, age: ageText(d.age, S, num) });
      txt.append(b, sm); head.append(img, txt);
      var dl = document.createElement('dl'); dl.className = 'bip-summary';
      dl.append(row(S.review_book, S.review_book_value), row(S.review_edition, d.gender === 'g' ? S.edition_g : S.edition_b),
        row(S.review_name_on_cover, cleanName(d.name)));
      var total = row(S.review_total, cfg.variant && cfg.variant.price_text ? cfg.variant.price_text : '');
      total.classList.add('bip-total'); dl.appendChild(total);
      review.replaceChildren(head, dl);
    }
    backBtn.addEventListener('click', function () { goStep(1); });
    // Which add button was used (e.submitter is not available everywhere).
    addBtn.addEventListener('click', function () { wantAnother = false; });
    if (anotherBtn) anotherBtn.addEventListener('click', function () { wantAnother = true; });
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var d = current(), p = validateColoring(d);
      if (step === 1) {
        if (p && p.field !== 'submit') return showProblem(p);
        renderReview(d); goStep(2); return;
      }
      if (p) return showProblem(p);
      if (preview) { setStatus(S.preview_no_cart); return; }
      nameIn.value = cleanName(nameIn.value);
      if (returnTo) returnTo.value = wantAnother && cfg.anotherUrl ? cfg.anotherUrl : (cfg.cartUrl || '/cart');
      setBusy([addBtn, anotherBtn, backBtn], true); setStatus(S.adding);
      nativeSubmit(form);
    });
    // Coming back from the cart with the browser's back button restores this page from cache.
    window.addEventListener('pageshow', function () { setBusy([addBtn, anotherBtn, backBtn], false); setStatus(''); });
    // Initial state without moving focus or scrolling.
    setSteps(root, 1); backBtn.hidden = true; addBtn.hidden = true; if (anotherBtn) anotherBtn.hidden = true;
    setEdition(root, gender() || cfg.defaultEdition || 'g');
  }

  // ---------------------------------------------------------------- Magic Me
  function initMagic(root, cfg) {
    var S = cfg.strings || {}, num = numberFormatter(cfg.numLocale), preview = cfg.mode === 'preview';
    var form = root.querySelector('[data-bip-form]'); if (!form) return;
    var q = function (sel) { return root.querySelector(sel); };
    var TEMPLATES = (cfg.templates || []).map(function (t) { return { id: t.id, gender: t.gender, title: t.title, src: t.src, alt: t.alt }; });
    var packs = resolvePacks(cfg.variants, cfg.packs);
    var store = createMagicStore(TEMPLATES), s = store.state;
    var P = (root.id || 'bip') + '-';
    var step = 1, tab = 0;
    var notes = {}, noteEls = {}, photoErrors = {}, keyed = new Map(), labelEls = [], tabEls = [];
    var slots = new Map(), memory = new Map();
    var park = q('[data-bip-files]'), propsBox = q('[data-bip-props]'), variantIn = form.querySelector('input[name="id"]');
    var errorEl = q('[data-bip-error]'), statusEl = q('[data-bip-status]');
    var backBtn = q('[data-bip-back]'), nextBtn = q('[data-bip-next]'), addBtn = q('[data-bip-add]');
    var STORY = function (i) { return S['story_' + (i + 1)] || ''; };
    var CHILD = function (i) { return S['child_' + (i + 1)] || ''; };
    var GW = { b: S.gender_b, g: S.gender_g };
    var money = function (cents) { return fmt(S.money_plain, { amount: num(cents / 100) }); };

    function el(tag, props) {
      var e = document.createElement(tag), kids = Array.prototype.slice.call(arguments, 2);
      Object.keys(props || {}).forEach(function (k) {
        var v = props[k];
        if (k === 'text') e.textContent = v;
        else if (k === 'key') { e.setAttribute('data-key', v); keyed.set(v, e); }
        else if (k.indexOf('on') === 0) e[k] = v;
        else if (v !== false && v != null) e.setAttribute(k, v === true ? '' : v);
      });
      (function add(list) { list.forEach(function (k) { if (Array.isArray(k)) add(k); else if (k != null && k !== false) e.append(k); }); })(kids);
      return e;
    }
    var childLabel = function (c) { return cleanName(c.name) || CHILD(s.children.indexOf(c)); };
    var childText = function (c, e) { labelEls.push({ e: e, id: c.id }); e.textContent = childLabel(c); return e; };
    var setError = function (m) { errorEl.textContent = m || ''; };
    var setStatus = function (m) { if (statusEl) statusEl.textContent = m || ''; };
    var noteText = function (code, c) { return fmt(S[code] || '', { child: c ? childLabel(c) : '' }); };

    // One persistent file input per child. It is moved into the visible dropzone, never re-created.
    function slotFor(c) {
      if (slots.has(c.id)) return slots.get(c.id);
      var input = el('input', { type: 'file', class: 'bip-file', accept: PHOTO_TYPES.join(','), id: P + 'photo-' + c.id, 'data-key': 'photo-' + c.id });
      input.addEventListener('change', function () { takePhoto(c.id, input.files && input.files[0], false); });
      slots.set(c.id, input); park.appendChild(input); return input;
    }
    function dropUnusedSlots() {
      slots.forEach(function (input, id) {
        if (!store.child(id)) { input.remove(); slots.delete(id); memory.delete(id); delete photoErrors[id]; }
      });
    }
    function takePhoto(childId, file, fromDrop) {
      var input = slots.get(childId); if (!file || !input) return;
      var err = checkPhoto(file);
      if (!err && fromDrop && !restoreFile(input, file)) err = 'photo_drop_failed';
      if (err) {
        photoErrors[childId] = S[err] || '';
        var prev = memory.get(childId);
        if (prev) { if (!restoreFile(input, prev)) input.value = ''; } else input.value = '';
      } else {
        store.setPhoto(childId, file, function (f) { return URL.createObjectURL(f); }, function (u) { URL.revokeObjectURL(u); });
        memory.set(childId, file); delete photoErrors[childId]; resolveGuidance(childId);
      }
      render();
    }

    function setNote(itemId, text, kind) { if (text) notes[itemId] = { text: text, kind: kind }; else delete notes[itemId]; }
    function noteFromAssign(itemId, code) {
      var c = store.child(store.item(itemId).childId);
      setNote(itemId, code ? noteText(code, c) : '', c && !c.gender ? 'needs-gender' : 'cover');
    }
    function resolveGuidance(childId) {
      var c = store.child(childId); if (!c) return;
      store.active().forEach(function (it) {
        var n = notes[it.id]; if (it.childId !== childId || !n) return;
        if ((n.kind === 'needs-gender' && c.gender) || (n.kind === 'needs-profile' && store.isComplete(c))) {
          delete notes[it.id]; if (noteEls[it.id]) noteEls[it.id].textContent = '';
        }
      });
    }
    // Any edit while reviewing sends the customer back to step 1, so the review is never stale.
    function invalidate() { if (step === 2) { goStep(1); setError(S.changed_review); } }

    function render() {
      var focusKey = document.activeElement && document.activeElement.getAttribute ? document.activeElement.getAttribute('data-key') : '';
      slots.forEach(function (input) { park.appendChild(input); });
      keyed.clear(); labelEls.length = 0; tabEls.length = 0;
      var items = store.active();
      if (tab >= items.length) tab = items.length - 1;
      var tabs = q('[data-bip-tabs]');
      tabs.hidden = items.length < 2; tabs.setAttribute('data-n', String(items.length));
      tabs.replaceChildren.apply(tabs, items.length < 2 ? [] : items.map(renderTab));
      q('[data-bip-items]').replaceChildren(renderItem(items[tab], tab));
      dropUnusedSlots(); renderTotals();
      slots.forEach(function (input) { keyed.set(input.getAttribute('data-key'), input); });
      if (focusKey && keyed.get(focusKey)) keyed.get(focusKey).focus();
    }
    function renderTab(it, i) {
      var c = store.child(it.childId);
      var e = el('button', { type: 'button', role: 'tab', class: 'bip-storytab', id: P + 'tab-' + it.id, key: 'tab-' + it.id,
        'aria-selected': String(i === tab), 'aria-controls': P + 'item-' + it.id, tabindex: i === tab ? '0' : '-1',
        onclick: function () { tab = i; render(); },
        onkeydown: function (ev) {
          var n = store.active().length, j = null, rtl = getComputedStyle(root).direction === 'rtl';
          if (ev.key === 'ArrowLeft') j = rtl ? (i + 1) % n : (i - 1 + n) % n;
          else if (ev.key === 'ArrowRight') j = rtl ? (i - 1 + n) % n : (i + 1) % n;
          else if (ev.key === 'Home') j = 0; else if (ev.key === 'End') j = n - 1;
          if (j === null) return; ev.preventDefault(); tab = j; render();
          var t = keyed.get('tab-' + store.active()[j].id); if (t) t.focus();
        } },
        el('span', { class: 'bip-tab-mark', 'aria-hidden': 'true' }, tickIcon()),
        el('span', { class: 'bip-tab-text' }, el('span', { class: 'bip-tab-name', text: STORY(i) }),
          el('span', { class: 'bip-tab-for' }, c ? fmt(S.for_child_prefix, {}) : '', c ? childText(c, el('span')) : '')),
        el('span', { class: 'bip-sr bip-tab-state' }));
      tabEls.push({ e: e, it: it }); updateTabState(e, it); return e;
    }
    function updateTabState(e, it) {
      var done = store.itemComplete(it); e.setAttribute('data-done', String(done));
      var sr = e.querySelector('.bip-tab-state'); if (sr) sr.textContent = done ? S.tab_done : S.tab_missing;
    }
    function updateTabStates() { tabEls.forEach(function (x) { updateTabState(x.e, x.it); }); }

    function renderItem(it, index) {
      var c = store.child(it.childId), isHome = store.homeOf(it.childId) === it, title = STORY(index);
      var whoButtons = s.children.map(function (ch) {
        return el('button', { type: 'button', class: 'bip-who', key: 'who-' + it.id + '-' + ch.id, 'aria-pressed': String(ch.id === it.childId),
          onclick: function () { if (ch.id === it.childId) return; noteFromAssign(it.id, store.assign(it.id, ch.id)); invalidate(); render(); } },
          childText(ch, el('span')));
      });
      if (store.canAddChild()) whoButtons.push(el('button', { type: 'button', class: 'bip-who bip-who-new', key: 'new-' + it.id,
        onclick: function () {
          var r = store.assignNewChild(it.id);
          if (r.error) setNote(it.id, S[r.error], 'cover');
          else if (r.note) noteFromAssign(it.id, r.note);
          else setNote(it.id, fmt(S.fill_child_below, { child: childLabel(r.child) }), 'needs-profile');
          invalidate(); render();
          var n = r.child && keyed.get('name-' + r.child.id); if (n) n.focus();
        } }, plusIcon(), el('span', { text: S.another_child })));
      var whoId = P + 'who-label-' + it.id, multi = store.active().length > 1;
      return el('div', { class: 'bip-story-item', id: P + 'item-' + it.id, role: multi ? 'tabpanel' : null, 'aria-labelledby': multi ? P + 'tab-' + it.id : null },
        el('p', { class: 'bip-q', id: whoId, text: fmt(S.story_for_whom, { story: title }) }),
        el('div', { class: 'bip-whofor', role: 'group', 'aria-labelledby': whoId }, whoButtons),
        index > 0 && s.children.length === 1 ? el('p', { class: 'bip-help', text: S.same_or_other }) : null,
        c && isHome ? renderProfile(c) : c ? renderProfileRef(c) : null,
        renderCovers(it, c),
        noteEls[it.id] = el('p', { class: 'bip-item-note', 'aria-live': 'polite', text: notes[it.id] ? notes[it.id].text : '' }));
    }
    function renderProfileRef(c) {
      var home = store.homeOf(c.id), homeIndex = store.active().indexOf(home);
      return el('div', { class: 'bip-profile-ref' },
        c.photoUrl ? el('img', { src: c.photoUrl, alt: fmt(S.photo_of, { child: childLabel(c) }), width: '48', height: '48' }) : null,
        el('p', {}, S.details_saved_prefix, ' ', childText(c, el('strong')), ' ', fmt(S.details_saved_in, { story: STORY(homeIndex) })),
        el('button', { type: 'button', class: 'bip-linkbtn', key: 'edit-' + c.id, text: fmt(S.edit_in, { story: STORY(homeIndex) }),
          onclick: function () { tab = homeIndex; render(); var n = keyed.get('name-' + c.id); if (n) n.focus(); } }));
    }
    function renderProfile(c) {
      var id = c.id, ageVal = String(c.age || DEFAULT_AGE), input = slotFor(c);
      input.setAttribute('aria-describedby', P + 'photo-hint-' + id + ' ' + P + 'photo-err-' + id);
      var thumb = el('span', { class: 'bip-thumb' }, c.photoUrl ? el('img', { src: c.photoUrl, alt: fmt(S.photo_of, { child: childLabel(c) }) }) : photoIcon());
      var zone = el('label', { class: 'bip-drop', for: input.id, 'data-has': String(!!c.hasPhoto),
        ondragover: function (e) { e.preventDefault(); zone.setAttribute('data-over', 'true'); },
        ondragleave: function () { zone.setAttribute('data-over', 'false'); },
        ondrop: function (e) { e.preventDefault(); zone.setAttribute('data-over', 'false'); takePhoto(id, e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0], true); } },
        thumb,
        el('span', { class: 'bip-dz-text' }, el('strong', { text: c.hasPhoto ? S.photo_change : S.photo_pick }),
          el('span', { class: 'bip-hint', id: P + 'photo-hint-' + id, text: S.photo_hint })),
        input);
      var readout = el('output', { for: P + 'age-' + id, class: 'bip-age-out', text: ageText(ageVal, S, num) });
      return el('div', { class: 'bip-profile' },
        el('p', { class: 'bip-profile-title' }, S.details_of, ' ', childText(c, el('span'))),
        el('div', { class: 'bip-field' }, el('label', { for: P + 'name-' + id, text: S.name_label }),
          el('input', { id: P + 'name-' + id, key: 'name-' + id, name: 'bip_child_name_' + id, type: 'text', maxlength: String(MAX_NAME), placeholder: S.name_placeholder, autocomplete: 'off', value: c.name,
            oninput: function (e) { store.setField(id, 'name', e.target.value); invalidate(); resolveGuidance(id); refreshLabels(); updateTabStates(); } })),
        el('div', { class: 'bip-field' },
          el('label', { for: P + 'age-' + id, class: 'bip-age-label' }, el('span', { text: S.age_label + ' ' }), readout),
          el('input', { id: P + 'age-' + id, key: 'age-' + id, name: 'bip_child_age_' + id, type: 'range', class: 'bip-age', min: String(MIN_AGE), max: String(MAX_AGE), step: '1', value: ageVal, 'aria-valuetext': ageText(ageVal, S, num),
            oninput: function (e) { store.setField(id, 'age', e.target.value); var t = ageText(e.target.value, S, num); readout.textContent = t; e.target.setAttribute('aria-valuetext', t); invalidate(); resolveGuidance(id); updateTabStates(); } }),
          el('div', { class: 'bip-age-scale', 'aria-hidden': 'true' }, el('span', { text: num(MIN_AGE) }), el('span', { text: num(MAX_AGE) }))),
        el('div', { class: 'bip-field' }, el('span', { class: 'bip-label', id: P + 'gender-label-' + id, text: S.gender_label }),
          el('div', { class: 'bip-gender', role: 'group', 'aria-labelledby': P + 'gender-label-' + id },
            GENDERS.map(function (g) {
              return el('button', { type: 'button', key: 'gender-' + id + '-' + g, name: 'bip_child_gender_' + id, value: g, 'aria-pressed': String(c.gender === g),
                onclick: function () {
                  if (c.gender === g) return;
                  var cleared = store.setGender(id, g); resolveGuidance(id);
                  cleared.forEach(function (it) { setNote(it.id, S.cover_cleared_gender_short, 'cover'); });
                  invalidate(); render();
                } }, el('span', { class: 'bip-g-mark', 'aria-hidden': 'true' }, tickIcon()), el('span', { text: GW[g] }));
            }))),
        el('div', { class: 'bip-field' }, el('span', { class: 'bip-label', text: S.photo_label }), zone,
          el('p', { class: 'bip-photo-err', id: P + 'photo-err-' + id, role: 'alert', text: photoErrors[id] || '' })));
    }
    function renderCovers(it, c) {
      if (!c || !c.gender) return el('p', { class: 'bip-help bip-cover-wait', key: 'cover-' + it.id, tabindex: '-1', text: S.pick_gender_first });
      var choices = TEMPLATES.filter(function (t) { return t.gender === c.gender; });
      return el('div', { class: 'bip-covers' },
        el('p', { class: 'bip-q', id: P + 'covers-label-' + it.id, text: S.pick_story }),
        el('div', { class: 'bip-storygrid', role: 'group', 'aria-labelledby': P + 'covers-label-' + it.id }, choices.map(function (t, i) {
          var dup = store.active().find(function (o) { return o !== it && o.childId === c.id && o.templateId === t.id; });
          return el('button', { type: 'button', class: 'bip-story', key: i === 0 ? 'cover-' + it.id : 'cover-' + it.id + '-' + t.id, name: 'bip_story_' + it.id, value: t.id,
            'aria-pressed': String(it.templateId === t.id), 'aria-disabled': dup ? 'true' : false,
            'aria-label': t.title + (dup ? fmt(S.taken_in_sr, { story: STORY(store.storyIndex(dup)) }) : ''),
            onclick: function () {
              if (it.templateId === t.id) return;
              var err = store.pick(it.id, t.id);
              setNote(it.id, err ? noteText(err, c) : '', 'cover'); if (!err) invalidate(); setError(''); render();
            } },
            el('img', { src: t.src, alt: '', width: '200', height: '200', loading: 'lazy' }),
            el('span', { class: 'bip-tick' }, tickIcon()),
            el('span', { class: 'bip-story-title', text: t.title }),
            dup ? el('span', { class: 'bip-story-taken', text: fmt(S.taken_in, { story: STORY(store.storyIndex(dup)) }) }) : null);
        })));
    }
    function renderTotals() {
      var p = packs[s.count];
      root.querySelectorAll('[data-bip-count]').forEach(function (b) { b.setAttribute('aria-pressed', String(Number(b.getAttribute('data-bip-count')) === s.count)); });
      root.querySelectorAll('[data-bip-total]').forEach(function (t) { t.textContent = p ? p.priceText : S.pack_unavailable; });
      if (variantIn) variantIn.value = p && p.available ? String(p.id) : '';
    }
    function refreshLabels() { labelEls.forEach(function (x) { var c = store.child(x.id); if (c) x.e.textContent = childLabel(c); }); }

    function problemText(p) {
      var c = p.childId ? store.child(p.childId) : null, t = p.templateId ? store.template(p.templateId) : null;
      return fmt(S[p.code] || '', { story: p.story != null ? STORY(p.story) : '', child: c ? childLabel(c) : '', title: t ? t.title : '', other: p.other != null ? STORY(p.other) : '' });
    }
    function showProblem(p) {
      if (step !== 1) goStep(1);
      setError(problemText(p));
      if (p.itemId) {
        var owner = p.childId && ['name', 'age', 'gender', 'photo'].indexOf(p.field) !== -1 ? store.homeOf(p.childId) : store.item(p.itemId);
        tab = Math.max(0, store.active().indexOf(owner)); render();
      }
      var keyFor = { name: 'name-' + p.childId, age: 'age-' + p.childId, gender: 'gender-' + p.childId + '-b', photo: 'photo-' + p.childId, cover: 'cover-' + p.itemId, who: 'who-' + p.itemId + '-' + (s.children[0] && s.children[0].id) };
      var target = p.field === 'consent' ? q('[data-bip-consent]') : keyed.get(keyFor[p.field]) || document.getElementById(P + 'item-' + p.itemId);
      if (target) { target.focus(); if (target.scrollIntoView) target.scrollIntoView({ block: 'center' }); }
    }
    function goStep(n) {
      step = n; setSteps(root, n);
      backBtn.hidden = n === 1; nextBtn.hidden = n !== 1; addBtn.hidden = n !== 2;
      setError(''); setStatus('');
      var h = root.querySelector('[data-bip-steptitle="' + n + '"]'); if (h) h.focus();
      scrollTo(form);
    }
    function renderReview() {
      var p = packs[s.count], box = q('[data-bip-review]');
      var row = function (dt, dd, cls) { return el('div', { class: 'bip-row' + (cls ? ' ' + cls : '') }, el('dt', { text: dt }), el('dd', { text: dd })); };
      box.replaceChildren(
        el('ul', { class: 'bip-review-stories' }, store.active().map(function (it, i) {
          var c = store.child(it.childId), t = store.template(it.templateId);
          return el('li', {},
            el('img', { src: t.src, alt: t.alt || t.title, width: '200', height: '200' }),
            el('div', { class: 'bip-rs-text' }, el('small', { text: STORY(i) }), el('b', { text: t.title }),
              el('span', { text: fmt(S.review_story_for, { name: cleanName(c.name), gender: GW[c.gender], age: ageText(c.age, S, num) }) })),
            el('button', { type: 'button', class: 'bip-linkbtn', 'aria-label': fmt(S.edit_story_sr, { story: STORY(i) }), text: S.edit, onclick: function () { tab = i; goStep(1); render(); } }));
        })),
        el('dl', { class: 'bip-summary' },
          row(S.review_pack, S['pack_' + s.count]),
          row(S.review_unit, p.count === 1 ? p.priceText : money(p.unit)),
          p.saving ? row(S.review_saving, money(p.saving)) : null,
          row(S.review_total, p.priceText, 'bip-total')),
        el('p', { class: 'bip-fine', text: S.review_note }));
    }
    function next() {
      var p = store.validate(); if (p) return showProblem(p);
      var pk = packs[s.count];
      if (!pk || !pk.available) { setError(S.pack_unavailable_error); return; }
      if (step === 1) { renderReview(); goStep(2); return; }
      if (preview) { setStatus(S.preview_no_cart); return; }
      var sub = buildMagicSubmission(store, S, num);
      var missing = prepareFileSlots(slots, sub.files, memory, restoreFile);
      if (missing.length) {
        var c = store.child(missing[0]); var home = c && store.homeOf(c.id);
        return showProblem({ code: 'photo_missing', field: 'photo', childId: missing[0], itemId: home && home.id, story: home ? store.storyIndex(home) : 0 });
      }
      variantIn.value = String(pk.id);
      propsBox.replaceChildren.apply(propsBox, sub.properties.map(function (kv) {
        var i = document.createElement('input'); i.type = 'hidden'; i.name = 'properties[' + kv[0] + ']'; i.value = kv[1]; return i;
      }));
      setBusy([addBtn, backBtn], true); setStatus(S.adding);
      nativeSubmit(form);
    }

    root.querySelectorAll('[data-bip-count]').forEach(function (b) {
      var pk = packs[Number(b.getAttribute('data-bip-count'))];
      if (!pk || !pk.available) { b.setAttribute('aria-disabled', 'true'); b.setAttribute('data-unavailable', 'true'); }
      var priceEl = b.querySelector('[data-bip-pack-price]'); if (priceEl) priceEl.textContent = pk && pk.available ? pk.priceText : S.pack_unavailable;
      var noteEl = b.querySelector('[data-bip-pack-note]');
      if (noteEl) noteEl.textContent = !pk || !pk.available ? '' : pk.count === 1 ? S.pack_note_single
        : fmt(pk.saving ? S.pack_note : S.pack_note_nosave, { unit: money(pk.unit), saving: money(pk.saving) });
      b.addEventListener('click', function () {
        var n = Number(b.getAttribute('data-bip-count'));
        if (!packs[n] || !packs[n].available) { setError(S.pack_unavailable_error); return; }
        if (n === s.count) return; store.setCount(n); invalidate(); setError(''); render();
      });
    });
    var consentEl = q('[data-bip-consent]');
    consentEl.addEventListener('change', function () { s.consent = consentEl.checked; invalidate(); });
    backBtn.addEventListener('click', function () { goStep(1); });
    form.addEventListener('submit', function (e) { e.preventDefault(); next(); });
    window.addEventListener('pageshow', function () { setBusy([addBtn, backBtn], false); setStatus(''); });
    // Start on the first available pack.
    if (!packs[1] || !packs[1].available) { var firstOk = [2, 3].find(function (n) { return packs[n] && packs[n].available; }); if (firstOk) store.setCount(firstOk); }
    setSteps(root, 1); backBtn.hidden = true; addBtn.hidden = true;
    render();
  }

  function boot() {
    document.querySelectorAll('[data-bip-root]').forEach(function (root) {
      if (root.getAttribute('data-bip-ready')) return;
      root.setAttribute('data-bip-ready', 'true');
      var cfg = readConfig(root);
      if (root.getAttribute('data-bip-mode') === 'magicme') initMagic(root, cfg); else initColoring(root, cfg);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})(typeof window !== 'undefined' ? window : globalThis);
